import { ChevronDown } from 'lucide-react';
import { CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export const detailsChevronClass =
	'h-4 w-4 transition-transform duration-200 group-data-[state=closed]:-rotate-90 max-md:size-5 max-md:text-muted-foreground/60';

/**
 * The title row of a collapsible details section. On desktop it is the small
 * chevron button beside the title, as before. On phones the whole row toggles
 * the section (at least 48px tall) with the disclosure chevron on the right;
 * `actions` (a + button, View all) stay separate buttons beside it. Wider
 * touch screens (iPad) keep the small chevron with a 44px touch area.
 */
export function DetailsSectionHeader({ title, actions, label, icon, className }) {
	return (
		<div
			className={cn(
				'relative flex items-center justify-between px-4 py-3 max-md:min-h-12 max-md:gap-2 max-md:py-1 max-md:pr-11',
				className,
			)}
		>
			<div className="flex items-center gap-2 max-md:min-w-0">
				<CollapsibleTrigger asChild>
					<button
						type="button"
						aria-label={label || (typeof title === 'string' ? title : 'Toggle section')}
						className="group h-5 w-5 text-muted-foreground transition-transform touch:md:relative touch:md:before:absolute touch:md:before:-inset-3 max-md:absolute max-md:inset-0 max-md:flex max-md:h-auto max-md:w-auto max-md:items-center max-md:justify-end max-md:pr-3 max-md:transition-colors max-md:focus-visible:outline-none max-md:focus-visible:ring-2 max-md:focus-visible:ring-inset max-md:focus-visible:ring-ring max-md:active:bg-muted/60"
					>
						{icon || <ChevronDown className={detailsChevronClass} />}
					</button>
				</CollapsibleTrigger>
				<h3 className="text-sm font-medium max-md:pointer-events-none max-md:relative max-md:truncate max-md:text-base">
					{title}
				</h3>
			</div>
			{actions}
		</div>
	);
}

/**
 * Wraps header actions so they sit above the whole-row toggle on phones. From
 * 768px up it generates no box, so the header lays out exactly as before.
 */
export function DetailsSectionActions({ className, children }) {
	return (
		<div
			className={cn(
				'md:contents max-md:relative max-md:z-[1] max-md:flex max-md:shrink-0 max-md:items-center max-md:gap-1',
				className,
			)}
		>
			{children}
		</div>
	);
}

/**
 * On the phone page the sections sit in inset rounded cards (an iOS grouped
 * list). Each section keeps its own divider; the card hides the last one and
 * disappears when none of its sections have anything to show.
 */
export function DetailsGroup({ isPage, className, children }) {
	if (!isPage) {
		return <>{children}</>;
	}

	return (
		<div
			className={cn(
				'mx-4 mt-4 overflow-hidden rounded-xl bg-background ring-1 ring-border/70 empty:hidden dark:bg-zinc-900 dark:ring-white/10 [&>*:last-child]:border-b-0',
				className,
			)}
		>
			{children}
		</div>
	);
}
